import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useIsModeDisabledByOrg } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useIsModeDisabledByOrg.js';
import { useSharedSelectSearchModel } from '#pplx/frontend/apps/ask/hooks/search-model-selection-hooks/useSharedSelectSearchModel.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { usePreferredSearchModelForSearchMode } from '#pplx/frontend/apps/ask/search/hooks/usePreferredSearchModelForSearchMode.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useCallback, useMemo } from 'react';
import { defineMessage, useIntl } from 'react-intl';
const BROWSER_AGENT_LOCKED_TOOLTIP = defineMessage({
    defaultMessage: 'Start a new thread to turn off browser control',
    id: '6da3199b91',
    description: 'Tooltip explaining why Browser Agent mode cannot be cleared in a committed thread',
});
function getSearchModeMenuProps({ $t, enabled, searchMode, onClearSearchMode, clearable, isFollowUp, canClearSearchMode, omittedSearchModes, }) {
    if (searchMode === SearchMode.SEARCH) {
        return { showSearchModeMenu: false };
    }
    if (searchMode === SearchMode.ASI && isFollowUp && !omittedSearchModes.includes(SearchMode.ASI)) {
        return { showSearchModeMenu: false };
    }
    // Computer can't be toggled off mid-thread; show a disabled tag.
    if (searchMode === SearchMode.ASI && isFollowUp) {
        return {
            showSearchModeMenu: true,
            searchMode,
            onClearSearchMode,
            clearable: false,
            disabled: true,
            disabledTooltip: 'Start a new thread to turn off computer',
        };
    }
    // Browser Agent mode: always show the chip (handled before the `!enabled` gate
    // so it never disappears). Locked once committed, clearable when fresh.
    if (searchMode === SearchMode.BROWSER_AGENT) {
        const isClearable = canClearSearchMode ?? !isFollowUp;
        return {
            showSearchModeMenu: true,
            searchMode,
            onClearSearchMode,
            clearable: isClearable,
            disabled: !isClearable,
            disabledTooltip: isClearable ? undefined : $t(BROWSER_AGENT_LOCKED_TOOLTIP),
        };
    }
    if (!enabled) {
        return { showSearchModeMenu: false };
    }
    return {
        showSearchModeMenu: true,
        searchMode,
        onClearSearchMode,
        clearable,
    };
}
export const useAskInputSearchModes = ({ enabled, searchMode: controlledSearchMode, onSearchModeSelect: controlledOnSearchModeSelect, omittedSearchModes = EMPTY_ARRAY, isFollowUp = false, canClearSearchMode, onSearchModeSelected, onSearchModeCleared, }) => {
    const { $t } = useIntl();
    const reason = 'use-ask-input-search-modes';
    const { getPreferredSearchModelForSearchMode } = usePreferredSearchModelForSearchMode();
    const internalSearchMode = useSelectedSearchMode();
    const openPaywallUpsell = usePaywallUpsell();
    const selectSearchModel = useSharedSelectSearchModel({ openPaywallUpsell });
    const { setExhaustedSearchMode, compareModels, setCompareModels } = useThreadConfig();
    const { getDefaultModelsForMode } = useSearchModels({ reason });
    const isSearchModeDisabled = useIsModeDisabledByOrg();
    const searchMode = controlledSearchMode ?? internalSearchMode;
    const handleSelectModelCouncil = useCallback(() => {
        // No need to do anything if compareModels is already set
        if (compareModels.length > 0) {
            return;
        }
        const defaultModelsForMode = getDefaultModelsForMode(SearchMode.AGENTIC_RESEARCH);
        setCompareModels(defaultModelsForMode);
    }, [getDefaultModelsForMode, compareModels, setCompareModels]);
    const internalOnSearchModeSelect = useCallback((newSearchMode) => {
        if (isFollowUp &&
            newSearchMode === SearchMode.ASI &&
            omittedSearchModes.includes(SearchMode.ASI)) {
            return false;
        }
        // Clear exhausted state when user selects any mode
        setExhaustedSearchMode(undefined);
        const targetMode = newSearchMode;
        const searchModel = getPreferredSearchModelForSearchMode(targetMode);
        // FIXME: Part of migrating to dynamic model config
        const applied = selectSearchModel(searchModel);
        if (!applied) {
            return false;
        }
        // If switching to model council and no compare models are selected set the default compare models from the config.
        if (targetMode === SearchMode.AGENTIC_RESEARCH) {
            handleSelectModelCouncil();
        }
        return true;
    }, [
        selectSearchModel,
        getPreferredSearchModelForSearchMode,
        setExhaustedSearchMode,
        handleSelectModelCouncil,
        isFollowUp,
        omittedSearchModes,
    ]);
    const onClearSearchMode = useCallback(() => {
        if (internalOnSearchModeSelect(SearchMode.SEARCH)) {
            onSearchModeCleared?.(SearchMode.SEARCH);
        }
    }, [internalOnSearchModeSelect, onSearchModeCleared]);
    const onSearchModeSelect = useCallback((newSearchMode) => {
        // Policy gate for every selection path — the disabled toggle/menu item
        // is only an affordance; direct callers (command palette, keyboard
        // shortcuts, pending-mode events) land here too.
        if (isSearchModeDisabled(newSearchMode)) {
            return;
        }
        if (controlledOnSearchModeSelect) {
            controlledOnSearchModeSelect(newSearchMode);
            onSearchModeSelected?.(newSearchMode);
            return;
        }
        if (internalOnSearchModeSelect(newSearchMode)) {
            onSearchModeSelected?.(newSearchMode);
        }
    }, [
        controlledOnSearchModeSelect,
        internalOnSearchModeSelect,
        isSearchModeDisabled,
        onSearchModeSelected,
    ]);
    const searchModeControllerProps = useMemo(() => {
        if (enabled) {
            return {
                showSearchMode: true,
                searchMode,
                onSearchModeSelect,
                omittedSearchModes,
                isSearchModeDisabled,
            };
        }
        return { showSearchMode: false };
    }, [enabled, searchMode, onSearchModeSelect, omittedSearchModes, isSearchModeDisabled]);
    const searchModeMenuProps = useMemo(() => getSearchModeMenuProps({
        $t,
        enabled,
        searchMode,
        onClearSearchMode,
        clearable: controlledSearchMode === undefined,
        isFollowUp,
        canClearSearchMode,
        omittedSearchModes,
    }), [
        $t,
        enabled,
        searchMode,
        onClearSearchMode,
        controlledSearchMode,
        isFollowUp,
        canClearSearchMode,
        omittedSearchModes,
    ]);
    return useMemo(() => ({
        searchModeControllerProps,
        searchModeMenuProps,
    }), [searchModeControllerProps, searchModeMenuProps]);
};
//# sourceMappingURL=useAskInputSearchModes.js.map